// ReactionForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/useAuthStore3';
import { WireguardConf } from '../../../types/vpn';

export interface VpnFormProps {
  wc: WireguardConf | null;
  onSave: (values: WireguardConf) => Promise<void> | void;  
  onCancel: () => void;
}

export const SelectByBool = (v: boolean, t: string, f: string, none: string) => {
  if (v === true) return t;
  if (v === false) return f;
  return none;
}

const VpnForm: React.FC<VpnFormProps> = ({
  wc,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const title = wc ? t('VpnForm.Edit_Configuration') : t('VpnForm.Add_New_Configuration');
  const { customerId } = useAuthStore();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testMeessage, setTestMeessage] = useState<string>('');
  const [testResultVisible, setTestResultVisible] = useState<boolean>(false);

  const initialValues: WireguardConf = wc || {
    customer_id: customerId || -1,
    id: -1,
    name: '',
    conf: '',
    enabled: false,
    comment: '',
    row_hash: '',
  };

  const validationSchema = Yup.object({
    // reaction_type_id: Yup.number().required(('Reaction_type_is_required')),
    name: Yup.string().required(('Name_is_required')),
    conf: Yup.string().required(('Configuration_is_required')),
    // param: Yup.mixed().required(('param_is_required')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => { // Додаємо async
        // Очищаємо попередню помилку
        setSaveError(null); 
        try {
          await Promise.resolve(onSave(values)); 
        } catch (error) {
          console.error("Помилка збереження WireguardConf:", error);
          const errorMessage = error instanceof Error ? error.message : t('OriginForm.Unknown_Save_Error');
          setSaveError(t('OriginForm.Save_Failed', { error: errorMessage }));
          setSubmitting(false); 
        }
      }}      
      enableReinitialize
    >
      {(formikProps: FormikProps<WireguardConf>) => {
        const { values, setFieldValue } = formikProps;

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            {saveError && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setSaveError(null)} // Дозволяємо закрити повідомлення
              >
                {saveError}
              </Alert>
            )}
            
            <Stack spacing={2} sx={{ mt: 2 }}>

              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('ReactionForm.Name_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('ReactionForm.Name_Helper_Text')}
                  />
                )}
              </Field>

              <Field name="conf">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={5}
                    label={t('VpnList.conf_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('VpnList.conf_Label')}
                  />
                )}
              </Field>
              
              <Field name="enabled" type="checkbox">
                {({ field }: { field: any }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('OriginForm.Enabled_Label')}
                  />
                )}
              </Field>

              <Field name="comment">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('VpnList.comment_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={(meta.touched && meta.error) || t('VpnList.comment_Label')}
                  />
                )}
              </Field>

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('OriginForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('OriginForm.Save_Origin')}
                </Button>
              </Box>
            </Box>

          </Form>
        );
      }}
    </Formik>
  );
};

export default VpnForm;